import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Copy, Check, Sparkles } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Message, parseChoiceQuestions } from '../types'
import { ChoiceCard } from './ChoiceCard'
import { ReasoningAccordion } from './ReasoningAccordion'
import { useTheme } from '@/context/ThemeContext'

const CHARS_PER_FRAME = 4 // ~240 chars/sec at 60fps

interface ParsedArtifact {
  id: string
  title: string
  type: string
  content: string
}

interface ParsedActionButton {
  type: string
  label: string
  payload: Record<string, any>
}

function getAttribute(tag: string, attr: string): string {
  const match = new RegExp(`${attr}=["']([^"']*)["']`, 'i').exec(tag)
  return match ? match[1] : ''
}

function parseArtifacts(text: string): { cleanText: string; artifacts: ParsedArtifact[] } {
  const artifacts: ParsedArtifact[] = []
  const tagStartRegex = /<artifact\s+([^>]+)>/gi
  let match
  let lastIndex = 0
  let cleanText = ''

  // 1. Explicit <artifact ...> tags
  while ((match = tagStartRegex.exec(text)) !== null) {
    let id = ''
    let title = ''
    let type = ''
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(match[0], 'text/html')
      const art = doc.querySelector('artifact')
      if (art) {
        id = art.getAttribute('id') || ''
        title = art.getAttribute('title') || ''
        type = art.getAttribute('type') || ''
      }
    } catch (err) {
      console.error('DOMParser failed to parse artifact tag:', match[0], err)
    }

    const startIdx = match.index
    const tagLength = match[0].length
    const contentStart = startIdx + tagLength

    const endTag = '</artifact>'
    const endIdx = text.indexOf(endTag, contentStart)

    let content = ''
    let matchEnd = 0

    if (endIdx !== -1) {
      content = text.slice(contentStart, endIdx)
      matchEnd = endIdx + endTag.length
    } else {
      content = text.slice(contentStart)
      matchEnd = text.length
    }

    cleanText += text.slice(lastIndex, startIdx)
    
    const artId = id || 'artifact-' + startIdx
    const artTitle = title || 'Visual Deliverable'
    
    artifacts.push({
      id: artId,
      title: artTitle,
      type: type || 'html',
      content: content.trim(),
    })

    cleanText += `\n\n[open-artifact:${artId}:${artTitle}]\n\n`
    lastIndex = matchEnd
  }

  cleanText += text.slice(lastIndex)

  // 2. Fallback: Sales deliverable code blocks (```sequence, ```dossier, ```battlecard, ```email, ```playbook)
  const deliverableBlockRegex = /```(sequence|dossier|battlecard|email|cadence|playbook|qualify)\n([\s\S]*?)```/gi
  let blockMatch
  while ((blockMatch = deliverableBlockRegex.exec(cleanText)) !== null) {
    const blockType = blockMatch[1]!.toLowerCase()
    const blockContent = blockMatch[2]!.trim()
    const artId = `deliverable-${artifacts.length + 1}`
    const artTitle = `${blockType.charAt(0).toUpperCase() + blockType.slice(1)} Deliverable`

    artifacts.push({
      id: artId,
      title: artTitle,
      type: blockType,
      content: blockContent,
    })

    cleanText = cleanText.replace(
      blockMatch[0],
      `\n\n[open-artifact:${artId}:${artTitle}]\n\n`
    )
  }

  return { cleanText, artifacts }
}

function parseActionButtons(text: string): { cleanText: string; actionButtons: ParsedActionButton[] } {
  const actionButtons: ParsedActionButton[] = []
  const tagRegex = /<action-button\s+([^>]+)>([\s\S]*?)<\/action-button>/gi
  
  const cleanText = text.replace(tagRegex, (match) => {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(match, 'text/html')
      const btn = doc.querySelector('action-button')
      if (btn) {
        const type = btn.getAttribute('type') || ''
        const label = btn.getAttribute('label') || btn.textContent?.trim() || ''
        const rawPayload = btn.getAttribute('payload')
        
        let payload = {}
        if (rawPayload) {
          try {
            payload = JSON.parse(rawPayload)
          } catch (err) {
            console.warn('Failed to parse action button payload:', rawPayload, err)
          }
        }
        actionButtons.push({ type, label, payload })
      }
    } catch (err) {
      console.error('DOMParser failed to parse action button:', match, err)
    }
    return ''
  })
  
  return { cleanText, actionButtons }
}

// Shared by both render paths below (ReactMarkdown's `p` override, and the raw
// streaming-text path) so a `[open-artifact:id:title]` marker always renders the
// same button — BUG-018 was specifically that the streaming path had no
// equivalent of this at all, so the marker sat there as literal bracket text.
const ArtifactOpenButton: React.FC<{
  artId: string
  artTitle: string
  artifacts: ParsedArtifact[]
  onOpenArtifact?: ChatMessageProps['onOpenArtifact']
}> = ({ artId, artTitle, artifacts, onOpenArtifact }) => {
  const { currentTheme } = useTheme()
  return (
    <div className="my-3">
      <button
        onClick={() => {
          const art = artifacts.find((a) => a.id === artId)
          if (art) onOpenArtifact?.(art.id, art.title, art.content)
        }}
        className="flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-semibold hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
        style={{
          backgroundColor: currentTheme.activeBackground,
          borderColor: currentTheme.accent,
          color: currentTheme.accent,
        }}
      >
        <Sparkles className="w-4 h-4 text-indigo-400 animate-pulse" />
        <span>View {artTitle}</span>
      </button>
    </div>
  )
}

const OPEN_ARTIFACT_MARKER = /\[open-artifact:([^:]+):([^\]]+)\]/g
// A marker that's still mid-typewriter-reveal (opening bracket seen, no closing
// ']' yet) — stripped from the streaming render rather than shown as raw text.
const OPEN_ARTIFACT_PARTIAL = /\[open-artifact:[^\]]*$/

// Splits streaming text on complete `[open-artifact:...]` markers and renders
// each as the real button immediately, instead of waiting for the whole
// message to finish streaming before ReactMarkdown gets a chance to.
function renderStreamingContent(
  text: string,
  artifacts: ParsedArtifact[],
  onOpenArtifact: ChatMessageProps['onOpenArtifact'],
): React.ReactNode[] {
  const visibleText = text.replace(OPEN_ARTIFACT_PARTIAL, '')
  const nodes: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  const re = new RegExp(OPEN_ARTIFACT_MARKER)
  while ((match = re.exec(visibleText)) !== null) {
    if (match.index > lastIndex) nodes.push(visibleText.slice(lastIndex, match.index))
    nodes.push(
      <ArtifactOpenButton
        key={`art-${match.index}`}
        artId={match[1]!}
        artTitle={match[2]!}
        artifacts={artifacts}
        onOpenArtifact={onOpenArtifact}
      />,
    )
    lastIndex = re.lastIndex
  }
  if (lastIndex < visibleText.length) nodes.push(visibleText.slice(lastIndex))
  return nodes
}

function useTypewriter(target: string, active: boolean): string {
  const [shown, setShown] = useState(active ? '' : target)
  const posRef = useRef(active ? 0 : target.length)
  const targetRef = useRef(target)
  const rafRef = useRef<number>()

  useEffect(() => { targetRef.current = target }, [target])

  useEffect(() => {
    if (!active) {
      setShown(target)
      posRef.current = target.length
      return
    }

    const step = () => {
      const t = targetRef.current
      if (posRef.current < t.length) {
        posRef.current = Math.min(posRef.current + CHARS_PER_FRAME, t.length)
        setShown(t.slice(0, posRef.current))
      }
      rafRef.current = requestAnimationFrame(step)
    }

    rafRef.current = requestAnimationFrame(step)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [active])

  return shown
}

interface ChatMessageProps {
  message: Message
  onOpenArtifact?: (id: string, title: string, content: string) => void
  onTriggerAction?: (type: string, payload: any) => void
  /** Whether an ask_user_choices card on this message can still be answered */
  choicesActive?: boolean
  onSubmitChoices?: (answer: string) => void
}

export const ChatMessage: React.FC<ChatMessageProps> = React.memo(({
  message,
  onOpenArtifact,
  onTriggerAction,
  choicesActive = false,
  onSubmitChoices,
}) => {
  const { currentTheme } = useTheme()
  const isUser = message.role === 'user'

  const { cleanText, artifacts } = parseArtifacts(message.content)
  const { cleanText: finalText, actionButtons } = parseActionButtons(cleanText)

  const choiceQuestions = message.thinking
    ?.filter((step) => step.toolName === 'ask_user_choices' && step.status === 'done')
    .map((step) => parseChoiceQuestions(step.result))
    .find((questions) => questions !== null) ?? null

  const streamingText = useTypewriter(finalText, message.isStreaming ?? false)
  const [copied, setCopied] = useState(false)

  const time = message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  function handleCopy() {
    navigator.clipboard.writeText(message.content).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (isUser) {
    return (
      <div className="flex justify-end px-4 py-1.5 max-w-3xl mx-auto w-full">
        <div className="flex flex-col items-end max-w-[65%]">
          <div
            className="px-4 py-2.5 rounded-2xl rounded-br-sm text-[15px] leading-7 whitespace-pre-wrap break-words"
            style={{
              background: currentTheme.userMessageBackground,
              color: '#FFFFFF',
            }}
          >
            {message.content}
          </div>
          <span className="text-[11px] mt-1 mr-0.5" style={{ color: currentTheme.mutedText }}>
            {time}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 py-2 max-w-3xl mx-auto w-full group">
      <div className="text-[15px] leading-7" style={{ color: currentTheme.Messagetext }}>
        {/* Reasoning trace accordion — live tool status badges during streaming & collapsible summary */}
        {message.thinking && message.thinking.length > 0 && (
          <ReasoningAccordion
            steps={message.thinking}
            isStreaming={message.isStreaming}
            thoughtDuration={message.thoughtDuration}
          />
        )}

        {message.content ? (
          message.isStreaming ? (
            <div className="whitespace-pre-wrap break-words">
              {renderStreamingContent(streamingText, artifacts, onOpenArtifact)}
              <span
                className="inline-block w-0.5 h-3.5 ml-px align-middle"
                style={{
                  backgroundColor: currentTheme.accent,
                  animation: 'zephBlink 0.75s ease-in-out infinite',
                }}
              />
            </div>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => {
                  const text = React.Children.toArray(children).join('')
                  if (text.startsWith('[open-artifact:')) {
                    const parts = text.slice(15, -1).split(':')
                    const artId = parts[0]!
                    const artTitle = parts[1]!
                    return (
                      <ArtifactOpenButton
                        artId={artId}
                        artTitle={artTitle}
                        artifacts={artifacts}
                        onOpenArtifact={onOpenArtifact}
                      />
                    )
                  }
                  return (
                    <p className="mb-2 last:mb-0 break-words" style={{ color: currentTheme.Messagetext }}>
                      {children}
                    </p>
                  )
                },
                a: ({ href, children }) => {
                  const linkStyle = { color: currentTheme.accent, textDecoration: 'underline', textUnderlineOffset: '2px' }
                  // In-app paths (e.g. a campaign Zeph just created) navigate client-side rather
                  // than reloading the page; anything else opens in a new tab.
                  if (href && href.startsWith('/') && !href.startsWith('//')) {
                    return <Link to={href} className="font-medium break-words" style={linkStyle}>{children}</Link>
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="break-words" style={linkStyle}>
                      {children}
                    </a>
                  )
                },
                strong: ({ children }) => (
                  <strong style={{ color: currentTheme.text, fontWeight: 600 }}>{children}</strong>
                ),
                em: ({ children }) => (
                  <em style={{ color: currentTheme.secondrytext }}>{children}</em>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc pl-4 mb-2 space-y-0.5">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal pl-4 mb-2 space-y-0.5">{children}</ol>
                ),
                li: ({ children }) => (
                  <li className="leading-relaxed" style={{ color: currentTheme.Messagetext }}>
                    {children}
                  </li>
                ),
                pre: ({ children }) => (
                  <pre
                    className="p-3 rounded-lg text-xs font-mono overflow-x-auto mb-2 custom-scrollbar"
                    style={{
                      backgroundColor: currentTheme.activeBackground,
                      color: currentTheme.text,
                    }}
                  >
                    {children}
                  </pre>
                ),
                code: ({ children, className }: any) => {
                  const isBlock = Boolean(className?.match(/language-/))
                  if (isBlock) {
                    return <code className="font-mono">{children}</code>
                  }
                  return (
                    <code
                      className="px-1.5 py-0.5 rounded text-[12px] font-mono"
                      style={{
                        backgroundColor: currentTheme.activeBackground,
                        color: currentTheme.accent,
                      }}
                    >
                      {children}
                    </code>
                  )
                },
                blockquote: ({ children }) => (
                  <blockquote
                    className="pl-3 my-2 italic"
                    style={{
                      borderLeft: `3px solid ${currentTheme.accent}`,
                      color: currentTheme.secondrytext,
                    }}
                  >
                    {children}
                  </blockquote>
                ),
                h1: ({ children }) => (
                  <h1 className="text-base font-bold mb-1.5" style={{ color: currentTheme.text }}>
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-sm font-semibold mb-1" style={{ color: currentTheme.text }}>
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-sm font-medium mb-1" style={{ color: currentTheme.text }}>
                    {children}
                  </h3>
                ),
                hr: () => <hr className="my-2" style={{ borderColor: currentTheme.border }} />,
                table: ({ children }) => (
                  <div className="overflow-x-auto mb-2">
                    <table
                      className="text-xs border-collapse w-full"
                      style={{ borderColor: currentTheme.border }}
                    >
                      {children}
                    </table>
                  </div>
                ),
                th: ({ children }) => (
                  <th
                    className="px-3 py-1.5 text-left font-semibold"
                    style={{
                      backgroundColor: currentTheme.activeBackground,
                      color: currentTheme.text,
                      border: `1px solid ${currentTheme.border}`,
                    }}
                  >
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td
                    className="px-3 py-1.5"
                    style={{
                      color: currentTheme.Messagetext,
                      border: `1px solid ${currentTheme.border}`,
                    }}
                  >
                    {children}
                  </td>
                ),
              }}
            >
              {finalText}
            </ReactMarkdown>
          )
        ) : (
          <span style={{ opacity: 0 }}>·</span>
        )}
      </div>

      {/* Action buttons list */}
      {actionButtons.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3 pl-9">
          {actionButtons.map((btn, idx) => (
            <button
              key={idx}
              onClick={() => onTriggerAction?.(btn.type, btn.payload)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold hover:scale-[1.03] active:scale-[0.97] transition-all duration-200"
              style={{
                backgroundColor: currentTheme.activeBackground,
                borderColor: currentTheme.border,
                color: currentTheme.text,
              }}
            >
              <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
              <span>{btn.label}</span>
            </button>
          ))}
        </div>
      )}

      {choiceQuestions && !message.isStreaming && (
        <ChoiceCard
          questions={choiceQuestions}
          active={choicesActive}
          onSubmit={(answer) => onSubmitChoices?.(answer)}
        />
      )}

      {/* Footer: avatar + timestamp, copy revealed on hover */}
      <div className="flex items-center gap-2 mt-2">
        <span
          className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: currentTheme.userMessageBackground }}
        >
          <img src="/Zeph-White-Logo.png" alt="Zeph" className="w-[70%] h-[70%] object-contain" />
        </span>
        <span className="text-[11px]" style={{ color: currentTheme.mutedText }}>
          {time}
        </span>
        {!message.isStreaming && (
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 text-[11px] opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            style={{ color: copied ? currentTheme.accent : currentTheme.mutedText }}
            title="Copy response"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        )}
      </div>
    </div>
  )
})

ChatMessage.displayName = 'ChatMessage'
